// src/services/modules.js
import {
    collection,
    doc,
    setDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    arrayUnion,
    arrayRemove,
    serverTimestamp
} from "firebase/firestore";
import { db, auth } from "../firebase";

/**
 * 1) Solicitar acesso a um módulo
 *    Cria um documento em users/{uid}/requests/{autoId}
 */
export async function requestModuleAccess(moduleId, reason = "") {
    const uid = auth.currentUser.uid;
    const colRef = collection(db, "users", uid, "requests");
    const reqData = {
        moduleId,
        reason,
        status: "pending",
        createdAt: serverTimestamp(),
        userId: uid
    };
    return await setDoc(doc(colRef), reqData);
}

/**
 * 2) Listar todos os módulos do usuário
 *    Retorna array [{ id, ...data }]
 */
export async function listUserModules() {
    const uid = auth.currentUser.uid;
    const colRef = collection(db, "users", uid, "modules");
    const snapshot = await getDocs(colRef);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * 2b) Obter um módulo do usuário
 *    Retorna { id, ...data } ou null se não existir
 */
export async function getUserModule(moduleId) {
    const uid = auth.currentUser.uid;
    // Cria a referência ao documento
    const userModuleRef = doc(db, "users", uid, "modules", moduleId);

    // Busca o documento
    const snap = await getDoc(userModuleRef);

    if (!snap.exists()) {
        return null;
    }
    // Retorna id e conteúdo
    return { id: snap.id, ...snap.data() };
}

/**
 * 3a) Adicionar um tópico (sub-módulo) a um módulo existente
 */
export async function addTopic(moduleId, topic) {
    const uid = auth.currentUser.uid;
    const ref = doc(db, "users", uid, "modules", moduleId);
    // tópico deve ser um objeto: { title, description, tarefas: [] }
    return await updateDoc(ref, {
        topicos: arrayUnion({ ...topic, tarefas: [] })
    });
}

/**
 * 3b) Remover um tópico existente
 */
export async function removeTopic(moduleId, topic) {
    const uid = auth.currentUser.uid;
    const ref = doc(db, "users", uid, "modules", moduleId);
    // topic precisa ser exatamente igual ao objeto gravado
    return await updateDoc(ref, {
        topicos: arrayRemove(topic)
    });
}

/**
 * 3c) Atualizar um tópico (merge parcial)
 */
export async function updateTopic(moduleId, topicIdx, data) {
    const uid = auth.currentUser.uid;
    const ref = doc(db, "users", uid, "modules", moduleId);
    // data é um objeto com as chaves a alterar em topicos[topicIdx]
    const fieldPath = `topicos.${topicIdx}`;
    return await updateDoc(ref, {
        [`${fieldPath}.${Object.keys(data).join("")}`]: data
    });
}

/**
 * 3d) Adicionar uma tarefa a um tópico específico
 *     task = { name, progress, description, acoes: [] }
 */
export async function addTask(moduleId, topicoIdx, task) {
    const uid = auth.currentUser.uid;
    const ref = doc(db, "users", uid, "modules", moduleId);
    const field = `topicos.${topicoIdx}.tarefas`;
    return await updateDoc(ref, {
        [field]: arrayUnion({ ...task, acoes: [] })
    });
}

/**
 * 3e) Remover uma tarefa existente
 */
export async function removeTask(moduleId, topicoIdx, task) {
    const uid = auth.currentUser.uid;
    const ref = doc(db, "users", uid, "modules", moduleId);
    const field = `topicos.${topicoIdx}.tarefas`;
    return await updateDoc(ref, {
        [field]: arrayRemove(task)
    });
}

/**
 * 3f) Atualizar uma tarefa (merge parcial)
 *    data = { name?, progress?, description? }
 */
export async function updateTask(moduleId, topicoIdx, tarefaIdx, data) {
    const uid = auth.currentUser.uid;
    const ref = doc(db, "users", uid, "modules", moduleId);
    const base = `topicos.${topicoIdx}.tarefas.${tarefaIdx}`;
    // monta vários campos para merge
    const updates = {};
    Object.entries(data).forEach(([key, value]) => {
        updates[`${base}.${key}`] = value;
    });
    return await updateDoc(ref, updates);
}

/**
 * 3g) Adicionar uma ação a uma tarefa
 *     action = { name, is_done, description }
 */
export async function addAction(moduleId, topicoIdx, tarefaIdx, action) {
    const uid = auth.currentUser.uid;
    const ref = doc(db, "users", uid, "modules", moduleId);
    const field = `topicos.${topicoIdx}.tarefas.${tarefaIdx}.acoes`;
    return await updateDoc(ref, {
        [field]: arrayUnion(action)
    });
}

/**
 * 3h) Remover uma ação de uma tarefa
 */
export async function removeAction(moduleId, topicoIdx, tarefaIdx, action) {
    const uid = auth.currentUser.uid;
    const ref = doc(db, "users", uid, "modules", moduleId);
    const field = `topicos.${topicoIdx}.tarefas.${tarefaIdx}.acoes`;
    return await updateDoc(ref, {
        [field]: arrayRemove(action)
    });
}

/**
 * 3i) Atualizar o status (is_done) de uma ação dentro de uma tarefa
 *     Usa arrayUnion/arrayRemove: remove objeto antigo, adiciona objeto atualizado
 */
export async function toggleActionDone(
    moduleId,
    topicoIdx,
    tarefaIdx,
    action,
    isDone
) {
    const uid = auth.currentUser.uid;
    const ref = doc(db, "users", uid, "modules", moduleId);
    const field = `topicos.${topicoIdx}.tarefas.${tarefaIdx}.acoes`;

    // Primeiro remove a versão antiga
    await updateDoc(ref, { [field]: arrayRemove(action) });
    // Depois adiciona a versão atualizada
    return await updateDoc(ref, {
        [field]: arrayUnion({ ...action, is_done: isDone })
    });
}

/**
 * 3j) Excluir módulo inteiro do usuário
 */
export async function deleteUserModule(moduleId) {
    const uid = auth.currentUser.uid;
    const ref = doc(db, "users", uid, "modules", moduleId);
    return await deleteDoc(ref);
}
